import {
  createGroup,
  createRouter,
  defineRoute,
  Link,
  param,
  preventDefaultLinkClickBehavior,
  type Route,
} from 'type-route'

import { SvgIcons, SvgType } from './components/Icon/Icon'

export { preventDefaultLinkClickBehavior }

const usersRoute = defineRoute('/users')
const usersCreateUserRoute = usersRoute.extend('/create')
const usersWithSelectedUserRoute = usersRoute.extend(
  {
    userId: param.path.number,
  },
  (pathParams) => `/${pathParams.userId}`,
)

const vehiclesRoute = defineRoute('/vehicles')
const createVehicleRoute = vehiclesRoute.extend('/create')
const specificVehiclePage = vehiclesRoute.extend(
  {
    vehicleId: param.path.number,
  },
  (pathParams) => `/${pathParams.vehicleId}`
)

const specificVehicleDetailsRoute = specificVehiclePage.extend('/details')
const specificVehiclePartsRoute = specificVehiclePage.extend('/parts')
const specificVehicleWithSelectedPartRoute = specificVehiclePartsRoute.extend(
  { partId: param.path.number },
  (pathParams) => `/${pathParams.partId}`
)
const specificVehicleWithNewPartRoute = specificVehiclePartsRoute.extend('/create')
const specificVehicleWithEditedPartRoute = specificVehicleWithSelectedPartRoute.extend('/edit')
const specificVehicleDiscrepancyReportsRoute = specificVehiclePage.extend('/discrepancy-reports')
const specificVehicleDiscrepancyReportsWithSelectedReportRoute = specificVehicleDiscrepancyReportsRoute.extend(
  { discrepancyReportId: param.path.number },
  (pathParams) => `/${pathParams.discrepancyReportId}`
)

const checklistsRoute = defineRoute('/checklists')
const checklistsCreateChecklistRoute = checklistsRoute.extend('/create')
const checklistsWithSelectedChecklistRoute = checklistsRoute.extend(
  {
    checklistId: param.path.number,
  },
  (pathParams) => `/${pathParams.checklistId}`,
)

const discrepancyReportsRoute = defineRoute('/discrepancy-reports')
const discrepancyReportsCreateReportRoute = discrepancyReportsRoute.extend('/create')
const discrepancyReportsWithSelectedReportIdRoute = discrepancyReportsRoute.extend(
  {
    selectedReportId: param.path.number,
  },
  (pathParams) => `/${pathParams.selectedReportId}`,
)

const partNumbersRoute = defineRoute('/part-numbers')
const partNumbersCreatePartNumbersRoute = partNumbersRoute.extend('/create')
const partNumbersWithSelectedPartNumbersRoute = partNumbersRoute.extend(
  {
    partNumberId: param.path.number,
  },
  (pathParams) => `/${pathParams.partNumberId}`,
)

const centerSettingsRoute = defineRoute('/center-settings')
const centerSettingsDetailsRoute = centerSettingsRoute.extend('/details')
const centerSettingsChecklistsRoute = centerSettingsRoute.extend('/checklists')
const centerSettingsNotificationsRoute = centerSettingsRoute.extend('/notifications')

export const flightNetworkRoute = defineRoute('/flight-network')

export const {
  RouteProvider,
  useRoute,
  routes,
  session: routerSession,
} = createRouter({
  home: defineRoute('/'),

  login: defineRoute('/login'),
  logout: defineRoute('/logout'),
  loginCallback: defineRoute(
    {
      code: param.query.optional.string,
    },
    () => '/login/callback',
  ),

  missionHistory: defineRoute('/mission-history'),

  discrepancyReportsRoute,
  discrepancyReportsCreateReportRoute,
  discrepancyReportsWithSelectedReportIdRoute,

  checklistReports: defineRoute('/checklist-reports'),

  centerSettingsDetailsRoute,
  centerSettingsChecklistsRoute,
  centerSettingsNotificationsRoute,

  flightNetworkRoute,

  vehiclesRoute,
  createVehicleRoute,
  specificVehiclePage,
  specificVehicleDetailsRoute,
  specificVehiclePartsRoute,
  specificVehicleWithSelectedPartRoute,
  specificVehicleWithNewPartRoute,
  specificVehicleWithEditedPartRoute,
  specificVehicleDiscrepancyReportsRoute,
  specificVehicleDiscrepancyReportsWithSelectedReportRoute,

  usersRoute,
  usersCreateUserRoute,
  usersWithSelectedUserRoute,

  checklistsRoute,
  checklistsCreateChecklistRoute,
  checklistsWithSelectedChecklistRoute,

  partNumbersRoute,
  partNumbersCreatePartNumbersRoute,
  partNumbersWithSelectedPartNumbersRoute,

})

export type AppRoute = Route<typeof routes>

export interface WithCurrentRoute {
  currentRoute: AppRoute
}

export interface WithDiscrepancyReportsRouteGroup {
  currentRoute: Route<typeof discrepancyReportsRouteGroup>
}

export interface WithCenterSettingsRoute {
  currentRoute: Route<typeof centerSettingsRouteGroup>
}

export interface WithLoginCallbackRoute {
  currentRoute: Route<typeof routes.loginCallback>
}

export interface WithUsersRoute {
  currentRoute: Route<typeof usersRouteGroup>
}

export interface WithVehiclesRoute {
  currentRoute: Route<typeof vehicleRouteGroup>
}

export interface WithChecklistsRoute {
  currentRoute: Route<typeof checklistsRouteGroup>
}

export interface WithPartNumbersRoute {
  currentRoute: Route<typeof partNumbersRouteGroup>
}

export interface WithDiscrepancyReportsRoute {
  currentRoute: Route<typeof discrepancyReportsRouteGroup>
}

export interface WithFlightNetworkRoute {
  currentRoute: Route<typeof flightNetworkRoute>
}

export const usersRouteGroup = createGroup([
  routes.usersRoute,
  routes.usersCreateUserRoute,
  routes.usersWithSelectedUserRoute,
])

export const partNumbersRouteGroup = createGroup([
  routes.partNumbersRoute,
  routes.partNumbersCreatePartNumbersRoute,
  routes.partNumbersWithSelectedPartNumbersRoute,
])

export const discrepancyReportsRouteGroup = createGroup([
  routes.discrepancyReportsRoute,
  routes.discrepancyReportsCreateReportRoute,
  routes.discrepancyReportsWithSelectedReportIdRoute,
])

export const checklistsRouteGroup = createGroup([
  routes.checklistsRoute,
  routes.checklistsCreateChecklistRoute,
  routes.checklistsWithSelectedChecklistRoute,
])

export const vehicleRouteGroup = createGroup([
  routes.vehiclesRoute,
  routes.createVehicleRoute,
  routes.specificVehiclePage,
  routes.specificVehicleDetailsRoute,
  routes.specificVehiclePartsRoute,
  routes.specificVehicleWithSelectedPartRoute,
  routes.specificVehicleWithNewPartRoute,
  routes.specificVehicleWithEditedPartRoute,
  routes.specificVehicleDiscrepancyReportsRoute,
  routes.specificVehicleDiscrepancyReportsWithSelectedReportRoute,
])

export const centerSettingsRouteGroup = createGroup([
  routes.centerSettingsDetailsRoute,
  routes.centerSettingsChecklistsRoute,
  routes.centerSettingsNotificationsRoute,
])

export const underConstructionRoutesGroup = createGroup([])

/* eslint-disable import/no-unused-modules, @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types -- We'll use these as soon as their pages are created; We prefer inference over repeating the same type info again */
export const homeLink = { ...routes.home() } as const
export const loginLink = { ...routes.login() } as const
export const logoutLink = { ...routes.logout() } as const
export const loginCallbackLink = { ...routes.loginCallback() } as const
export const missionHistoryLink = {
  ...routes.missionHistory(),
  text: 'Mission History',
  Icon: SvgIcons.MissionHistoryArrowSvg,
} as const

export const checklistReportsLink = {
  ...routes.checklistReports(),
  text: 'Checklist Reports',
  Icon: SvgIcons.ChecklistReportSvg,
} as const

export const centerSettingsLink = {
  ...routes.centerSettingsDetailsRoute(),
  text: 'Center Settings',
  Icon: SvgIcons.GearSvg,
  routeNames: centerSettingsRouteGroup.routeNames
} as const
export const flightNetworkLink = {
  ...routes.flightNetworkRoute(),
  text: 'Flight Network',
  Icon: SvgIcons.FlightNetworkSvg,
} as const

export const vehiclesPageLink = { 
  ...routes.vehiclesRoute(), 
  text: 'Vehicles', 
  Icon: SvgIcons.VehicleSvg,
  routeNames: vehicleRouteGroup.routeNames
} as const
export const vehiclesPageWithCreateNewVehicleLink = { ...routes.createVehicleRoute() } as const

export const getLinkForVehiclesPageWithSelectedVehicle = (vehicleId: number) => ({ ...routes.specificVehiclePage({ vehicleId }), text: `Vehicle ${vehicleId}`,})

export const getLinkForVehiclesPageWithSelectedVehicleDetails = (vehicleId: number) => ({ ...routes.specificVehicleDetailsRoute({ vehicleId }) })
export const getLinkForVehiclesPageWithSelectedVehicleParts = (vehicleId: number) => ({ ...routes.specificVehiclePartsRoute({ vehicleId }) })
export const getLinkForVehiclesPageWithSelectedVehicleAndPart = (vehicleId: number, partId: number) => ({ ...routes.specificVehicleWithSelectedPartRoute({ vehicleId, partId })})
export const getLinkForVehiclesPageWithSpecificVehicleAndNewPart = (vehicleId: number) => ({ ...routes.specificVehicleWithNewPartRoute({ vehicleId }) })
export const getLinkForVehiclesPageWithSpecificVehicleAndEditedPart = (vehicleId: number, partId: number) => ({ ...routes.specificVehicleWithEditedPartRoute({ vehicleId, partId }) })
export const getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports = (vehicleId: number) => ({ ...routes.specificVehicleDiscrepancyReportsRoute({ vehicleId }) })
export const getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport = (vehicleId: number, discrepancyReportId: number) => ({ ...routes.specificVehicleDiscrepancyReportsWithSelectedReportRoute({ vehicleId, discrepancyReportId }) })

export const getLinkForCenterSettingsDetails = {...routes.centerSettingsDetailsRoute() } as const
export const getLinkForCenterSettingsChecklists = {...routes.centerSettingsChecklistsRoute() } as const
export const getLinkForCenterSettingsNotifications = {...routes.centerSettingsNotificationsRoute() } as const

export const usersLink = {
  ...routes.usersRoute(),
  text: 'Users',
  Icon: SvgIcons.UserSvg,
  routeNames: usersRouteGroup.routeNames,
} as const
export const usersCreateUserLink = { ...routes.usersCreateUserRoute() } as const
export const getUsersPageWithSelectedUserLink = (userId: number) => ({
  ...routes.usersWithSelectedUserRoute({ userId }),
  text: `User ${userId}`,
})

export const checklistsLink = {
  ...routes.checklistsRoute(),
  text: 'Checklists',
  Icon: SvgIcons.ChecklistSvg,
  routeNames: checklistsRouteGroup.routeNames,
} as const
export const createChecklistLink = { ...routes.checklistsCreateChecklistRoute() } as const
export const getChecklistsPageWithSelectedChecklistsLink = (checklistId: number) => ({
  ...routes.checklistsWithSelectedChecklistRoute({ checklistId }),
  text: `Checklist ${checklistId}`,
})

export const partNumbersLink = { 
  ...routes.partNumbersRoute(), 
  text: 'Part Numbers', 
  Icon: SvgIcons.ScrewSvg,
  routeNames: partNumbersRouteGroup.routeNames,
} as const
export const partNumbersCreatePartNumbersLink = { ...routes.partNumbersCreatePartNumbersRoute() } as const
export const getPartNumbersPageWithSelectedPartNumbersLink = (partNumberId: number) => ({
  ...routes.partNumbersWithSelectedPartNumbersRoute({ partNumberId }),
  text: `Part number ${partNumberId}`,
})

export const discrepancyReportsLink = { 
  ...routes.discrepancyReportsRoute(), 
  text: 'Discrepancy Reports', 
  Icon: SvgIcons.DiscrepancyReportSvg,
  routeNames: discrepancyReportsRouteGroup.routeNames,
} as const
export const discrepancyReportsCreateDiscrepancyReportLink = { ...routes.discrepancyReportsCreateReportRoute() } as const
export const getDiscrepancyReportsPageWithSelectedDiscrepancyReportsLink = (selectedReportId: number) => ({
  ...routes.discrepancyReportsWithSelectedReportIdRoute({ selectedReportId }),
  text: `Selected Report Id ${selectedReportId}`,
})

/* eslint-enable -- End of disable block */

export const appSectionsRouteGroup = createGroup([
  routes.missionHistory,
  routes.logout,
  centerSettingsRouteGroup,
  checklistsRouteGroup,
  vehicleRouteGroup,
  partNumbersRouteGroup,
  discrepancyReportsRouteGroup,
  routes.checklistReports,
  routes.flightNetworkRoute,
  usersRouteGroup,
])

export interface WithAppSectionRoute {
  currentRoute: Route<typeof appSectionsRouteGroup>
}

export interface NavbarLink extends WithText, WithHref {
  name: string
  Icon: SvgType
  link: Link
  isHidden?: () => boolean,
  routeNames?: string[]
}

export const mainNavigationMenuButtonGroups: ReadonlyArray<ReadonlyArray<NavbarLink>> = [
  [missionHistoryLink, flightNetworkLink],
  [vehiclesPageLink, partNumbersLink, discrepancyReportsLink],
  [checklistsLink, checklistReportsLink],
  [usersLink, centerSettingsLink],
] as const
